import { forbidExtraProps, mutuallyExclusiveProps, nonNegativeInteger } from 'airbnb-prop-types';
import isTouchDevice from 'is-touch-device';
import { DateTime } from 'luxon';
import values from 'object.values';
import PropTypes from 'prop-types';
import React from 'react';
import { DayPickerPhrases } from '../defaultPhrases';
import getPhrasePropTypes from '../utils/getPhrasePropTypes';

import isAfterDay from '../utils/isAfterDay';
import isBeforeDay from '../utils/isBeforeDay';
import isInclusivelyAfterDay from '../utils/isInclusivelyAfterDay';
import isNextDay from '../utils/isNextDay';
import isPreviousDay from '../utils/isPreviousDay';
import isSameDay from '../utils/isSameDay';

import getVisibleDays from '../utils/getVisibleDays';
import isDayVisible from '../utils/isDayVisible';

import getSelectedDateOffset from '../utils/getSelectedDateOffset';

import { addModifier, deleteModifier } from '../utils/modifiers';
import toISODateString from '../utils/toISODateString';

import CalendarInfoPositionShape from '../shapes/CalendarInfoPositionShape';
import DayOfWeekShape from '../shapes/DayOfWeekShape';
import DisabledShape from '../shapes/DisabledShape';
import FocusedInputShape from '../shapes/FocusedInputShape';
import NavPositionShape from '../shapes/NavPositionShape';
import ScrollableOrientationShape from '../shapes/ScrollableOrientationShape';

import {
    DAY_SIZE,
    END_DATE,
    HORIZONTAL_ORIENTATION,
    INFO_POSITION_BOTTOM,
    NAV_POSITION_TOP,
    START_DATE,
    VERTICAL_SCROLLABLE,
} from '../constants';

import cloneDateTime from '../utils/cloneDateTime';
import getPooledLuxon from '../utils/getPooledLoxun';
import isFirstDayOfWeek from '../utils/isFirstDayOfWeek';
import isLastDayOfWeek from '../utils/isLastDayOfWeek';
import DayPicker from './DayPicker';

const propTypes = forbidExtraProps({
    startDate: PropTypes.instanceOf(DateTime),
    endDate: PropTypes.instanceOf(DateTime),
    onDatesChange: PropTypes.func,
    startDateOffset: PropTypes.func,
    endDateOffset: PropTypes.func,
    minDate: PropTypes.instanceOf(DateTime),
    maxDate: PropTypes.instanceOf(DateTime),

    focusedInput: FocusedInputShape,
    onFocusChange: PropTypes.func,
    onClose: PropTypes.func,

    keepOpenOnDateSelect: PropTypes.bool,
    minimumNights: PropTypes.number,
    disabled: DisabledShape,
    isOutsideRange: PropTypes.func,
    isDayBlocked: PropTypes.func,
    isDayHighlighted: PropTypes.func,
    getMinNightsForHoverDate: PropTypes.func,
    daysViolatingMinNightsCanBeClicked: PropTypes.bool,

    // DayPicker props
    renderMonthText: mutuallyExclusiveProps(PropTypes.func, 'renderMonthText', 'renderMonthElement'),
    renderMonthElement: mutuallyExclusiveProps(PropTypes.func, 'renderMonthText', 'renderMonthElement'),
    renderWeekHeaderElement: PropTypes.func,
    enableOutsideDays: PropTypes.bool,
    numberOfMonths: PropTypes.number,
    orientation: ScrollableOrientationShape,
    withPortal: PropTypes.bool,
    initialVisibleMonth: PropTypes.func,
    hideKeyboardShortcutsPanel: PropTypes.bool,
    daySize: nonNegativeInteger,
    noBorder: PropTypes.bool,
    verticalBorderSpacing: nonNegativeInteger,
    horizontalMonthPadding: nonNegativeInteger,

    dayPickerNavigationInlineStyles: PropTypes.object,
    navPosition: NavPositionShape,
    navPrev: PropTypes.node,
    navNext: PropTypes.node,
    renderNavPrevButton: PropTypes.func,
    renderNavNextButton: PropTypes.func,
    noNavButtons: PropTypes.bool,
    noNavNextButton: PropTypes.bool,
    noNavPrevButton: PropTypes.bool,

    onPrevMonthClick: PropTypes.func,
    onNextMonthClick: PropTypes.func,
    onOutsideClick: PropTypes.func,
    renderCalendarDay: PropTypes.func,
    renderDayContents: PropTypes.func,
    renderCalendarInfo: PropTypes.func,
    renderKeyboardShortcutsButton: PropTypes.func,
    renderKeyboardShortcutsPanel: PropTypes.func,
    calendarInfoPosition: CalendarInfoPositionShape,
    firstDayOfWeek: DayOfWeekShape,
    verticalHeight: nonNegativeInteger,
    transitionDuration: nonNegativeInteger,

    // accessibility
    onBlur: PropTypes.func,
    isFocused: PropTypes.bool,
    showKeyboardShortcuts: PropTypes.bool,
    onTab: PropTypes.func,
    onShiftTab: PropTypes.func,

    // i18n
    monthFormat: PropTypes.string,
    weekDayFormat: PropTypes.string,
    phrases: PropTypes.shape(getPhrasePropTypes(DayPickerPhrases)),
    dayAriaLabelFormat: PropTypes.string,

    isRTL: PropTypes.bool,
});

const defaultProps = {
    startDate: undefined, // TODO: use null
    endDate: undefined, // TODO: use null
    minDate: null,
    maxDate: null,
    onDatesChange() {},
    startDateOffset: undefined,
    endDateOffset: undefined,

    focusedInput: null,
    onFocusChange() {},
    onClose() {},

    keepOpenOnDateSelect: false,
    minimumNights: 1,
    disabled: false,
    isOutsideRange() {},
    isDayBlocked() {},
    isDayHighlighted() {},
    getMinNightsForHoverDate() {},
    daysViolatingMinNightsCanBeClicked: false,

    // DayPicker props
    renderMonthText: null,
    renderWeekHeaderElement: null,
    enableOutsideDays: false,
    numberOfMonths: 1,
    orientation: HORIZONTAL_ORIENTATION,
    withPortal: false,
    hideKeyboardShortcutsPanel: false,
    initialVisibleMonth: null,
    daySize: DAY_SIZE,

    dayPickerNavigationInlineStyles: null,
    navPosition: NAV_POSITION_TOP,
    navPrev: null,
    navNext: null,
    renderNavPrevButton: null,
    renderNavNextButton: null,
    noNavButtons: false,
    noNavNextButton: false,
    noNavPrevButton: false,

    onPrevMonthClick() {},
    onNextMonthClick() {},
    onOutsideClick() {},

    renderCalendarDay: undefined,
    renderDayContents: null,
    renderCalendarInfo: null,
    renderMonthElement: null,
    renderKeyboardShortcutsButton: undefined,
    renderKeyboardShortcutsPanel: undefined,
    calendarInfoPosition: INFO_POSITION_BOTTOM,
    firstDayOfWeek: null,
    verticalHeight: null,
    noBorder: false,
    transitionDuration: undefined,
    verticalBorderSpacing: undefined,
    horizontalMonthPadding: 13,

    // accessibility
    onBlur() {},
    isFocused: false,
    showKeyboardShortcuts: false,
    onTab() {},
    onShiftTab() {},

    // i18n
    monthFormat: 'MMMM yyyy',
    weekDayFormat: 'ccc',
    phrases: DayPickerPhrases,
    dayAriaLabelFormat: undefined,

    isRTL: false,
};

const getChooseAvailableDatePhrase = (phrases, focusedInput) => {
    if (focusedInput === START_DATE) {
        return phrases.chooseAvailableStartDate;
    }
    if (focusedInput === END_DATE) {
        return phrases.chooseAvailableEndDate;
    }
    return phrases.chooseAvailableDate;
};

export default class DayPickerRangeController extends React.PureComponent {
    constructor(props) {
        super(props);

        this.isTouchDevice = isTouchDevice();
        this.today = DateTime.now();
        this.modifiers = {
            today: (day) => this.isToday(day),
            blocked: (day) => this.isBlocked(day),
            'blocked-calendar': (day) => props.isDayBlocked(day),
            'blocked-out-of-range': (day) => props.isOutsideRange(day),
            'highlighted-calendar': (day) => props.isDayHighlighted(day),
            valid: (day) => !this.isBlocked(day),
            'selected-start': (day) => this.isStartDate(day),
            'selected-end': (day) => this.isEndDate(day),
            'blocked-minimum-nights': (day) => this.doesNotMeetMinimumNights(day),
            'selected-span': (day) => this.isInSelectedSpan(day),
            'last-in-range': (day) => this.isLastInRange(day),
            hovered: (day) => this.isHovered(day),
            'hovered-span': (day) => this.isInHoveredSpan(day),
            'hovered-offset': (day) => this.isInHoveredSpan(day),
            'after-hovered-start': (day) => this.isDayAfterHoveredStartDate(day),
            'first-day-of-week': (day) => isFirstDayOfWeek(day),
            'last-day-of-week': (day) => isLastDayOfWeek(day),
            'hovered-start-first-possible-end': (day, hoverDate) =>
                this.isFirstPossibleEndDateForHoveredStartDate(day, hoverDate),
            'hovered-start-blocked-minimum-nights': (day, hoverDate) =>
                this.doesNotMeetMinNightsForHoveredStartDate(day, hoverDate),
            'before-hovered-end': (day) => this.isDayBeforeHoveredEndDate(day),
            'no-selected-start-before-selected-end': (day) => this.beforeSelectedEnd(day) && !props.startDate,
            'selected-start-in-hovered-span': (day, hoverDate) => this.isStartDate(day) && isAfterDay(hoverDate, day),
            'selected-start-no-selected-end': (day) => this.isStartDate(day) && !props.endDate,
            'selected-end-no-selected-start': (day) => this.isEndDate(day) && !props.startDate,
        };

        const { currentMonth, visibleDays } = this.getStateForNewMonth(props);

        // initialize phrases
        // set the appropriate CalendarDay phrase based on focusedInput
        const chooseAvailableDate = getChooseAvailableDatePhrase(props.phrases, props.focusedInput);

        this.state = {
            hoverDate: null,
            currentMonth,
            phrases: {
                ...props.phrases,
                chooseAvailableDate,
            },
            visibleDays,
            disablePrev: this.shouldDisableMonthNavigation(props.minDate, currentMonth),
            disableNext: this.shouldDisableMonthNavigation(props.maxDate, currentMonth),
        };

        this.onDayClick = this.onDayClick.bind(this);
        this.onDayMouseEnter = this.onDayMouseEnter.bind(this);
        this.onDayMouseLeave = this.onDayMouseLeave.bind(this);
        this.onPrevMonthClick = this.onPrevMonthClick.bind(this);
        this.onNextMonthClick = this.onNextMonthClick.bind(this);
        this.onMonthChange = this.onMonthChange.bind(this);
        this.onYearChange = this.onYearChange.bind(this);
        this.onGetNextScrollableMonths = this.onGetNextScrollableMonths.bind(this);
        this.onGetPrevScrollableMonths = this.onGetPrevScrollableMonths.bind(this);
        this.getFirstFocusableDay = this.getFirstFocusableDay.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const {
            startDate,
            endDate,
            focusedInput,
            getMinNightsForHoverDate,
            minimumNights,
            isOutsideRange,
            isDayBlocked,
            isDayHighlighted,
            phrases,
            initialVisibleMonth,
            numberOfMonths,
            enableOutsideDays,
        } = nextProps;

        const {
            startDate: prevStartDate,
            endDate: prevEndDate,
            focusedInput: prevFocusedInput,
            minimumNights: prevMinimumNights,
            isOutsideRange: prevIsOutsideRange,
            isDayBlocked: prevIsDayBlocked,
            isDayHighlighted: prevIsDayHighlighted,
            phrases: prevPhrases,
            initialVisibleMonth: prevInitialVisibleMonth,
            numberOfMonths: prevNumberOfMonths,
            enableOutsideDays: prevEnableOutsideDays,
        } = this.props;

        const { hoverDate } = this.state;
        let { visibleDays } = this.state;

        let recomputeOutsideRange = false;
        let recomputeDayBlocked = false;
        let recomputeDayHighlighted = false;

        if (isOutsideRange !== prevIsOutsideRange) {
            this.modifiers['blocked-out-of-range'] = (day) => isOutsideRange(day);
            recomputeOutsideRange = true;
        }

        if (isDayBlocked !== prevIsDayBlocked) {
            this.modifiers['blocked-calendar'] = (day) => isDayBlocked(day);
            recomputeDayBlocked = true;
        }

        if (isDayHighlighted !== prevIsDayHighlighted) {
            this.modifiers['highlighted-calendar'] = (day) => isDayHighlighted(day);
            recomputeDayHighlighted = true;
        }

        const recomputePropModifiers = recomputeOutsideRange || recomputeDayBlocked || recomputeDayHighlighted;

        const didStartDateChange = startDate !== prevStartDate;
        const didEndDateChange = endDate !== prevEndDate;
        const didFocusChange = focusedInput !== prevFocusedInput;

        if (
            numberOfMonths !== prevNumberOfMonths ||
            enableOutsideDays !== prevEnableOutsideDays ||
            (initialVisibleMonth !== prevInitialVisibleMonth && !prevFocusedInput && didFocusChange)
        ) {
            const newMonthState = this.getStateForNewMonth(nextProps);
            const { currentMonth } = newMonthState;
            ({ visibleDays } = newMonthState);
            this.setState({
                currentMonth,
                visibleDays,
            });
        }

        let modifiers = {};

        if (didStartDateChange) {
            modifiers = this.deleteModifier(modifiers, prevStartDate, 'selected-start');
            modifiers = this.addModifier(modifiers, startDate, 'selected-start');

            if (prevStartDate) {
                const startSpan = cloneDateTime(prevStartDate).plus({ days: 1 });
                const endSpan = cloneDateTime(prevStartDate).plus({ days: prevMinimumNights + 1 });
                modifiers = this.deleteModifierFromRange(modifiers, startSpan, endSpan, 'after-hovered-start');

                if (!endDate || !prevEndDate) {
                    modifiers = this.deleteModifier(modifiers, prevStartDate, 'selected-start-no-selected-end');
                }
            }

            if (!prevStartDate && endDate && startDate) {
                modifiers = this.deleteModifier(modifiers, endDate, 'selected-end-no-selected-start');
                modifiers = this.deleteModifier(modifiers, endDate, 'selected-end-in-hovered-span');

                values(visibleDays).forEach((days) => {
                    Object.keys(days).forEach((day) => {
                        const dateTimeObj = DateTime.fromISO(day); // Convert string to Luxon DateTime
                        modifiers = this.deleteModifier(
                            modifiers,
                            dateTimeObj,
                            'no-selected-start-before-selected-end'
                        );
                    });
                });
            }
        }

        if (didEndDateChange) {
            modifiers = this.deleteModifier(modifiers, prevEndDate, 'selected-end');
            modifiers = this.addModifier(modifiers, endDate, 'selected-end');

            if (prevEndDate && (!startDate || !prevStartDate)) {
                modifiers = this.deleteModifier(modifiers, prevEndDate, 'selected-end-no-selected-start');
            }
        }

        if (didStartDateChange || didEndDateChange) {
            if (prevStartDate && prevEndDate) {
                modifiers = this.deleteModifierFromRange(
                    modifiers,
                    prevStartDate,
                    cloneDateTime(prevEndDate).plus({ days: 1 }),
                    'selected-span'
                );
            }

            if (startDate && endDate) {
                modifiers = this.deleteModifierFromRange(
                    modifiers,
                    startDate,
                    cloneDateTime(endDate).plus({ days: 1 }),
                    'hovered-span'
                );

                modifiers = this.addModifierToRange(
                    modifiers,
                    cloneDateTime(startDate).plus({ days: 1 }),
                    endDate,
                    'selected-span'
                );
            }

            if (startDate && !endDate) {
                modifiers = this.addModifier(modifiers, startDate, 'selected-start-no-selected-end');
            }

            if (endDate && !startDate) {
                modifiers = this.addModifier(modifiers, endDate, 'selected-end-no-selected-start');
            }

            if (!startDate && endDate) {
                values(visibleDays).forEach((days) => {
                    Object.keys(days).forEach((day) => {
                        const dateTimeObj = DateTime.fromISO(day); // Convert string to Luxon DateTime

                        if (isBeforeDay(dateTimeObj, endDate)) {
                            modifiers = this.addModifier(
                                modifiers,
                                dateTimeObj,
                                'no-selected-start-before-selected-end'
                            );
                        }
                    });
                });
            }
        }

        if (!this.isTouchDevice && didStartDateChange && startDate && !endDate) {
            const startSpan = cloneDateTime(startDate).plus({ days: 1 });
            const endSpan = cloneDateTime(startDate).plus({ days: minimumNights + 1 });
            modifiers = this.addModifierToRange(modifiers, startSpan, endSpan, 'after-hovered-start');
        }

        if (!this.isTouchDevice && didEndDateChange && !startDate && endDate) {
            const startSpan = cloneDateTime(endDate).minus({ days: minimumNights });
            const endSpan = cloneDateTime(endDate);
            modifiers = this.addModifierToRange(modifiers, startSpan, endSpan, 'before-hovered-end');
        }

        if (prevMinimumNights > 0) {
            if (didFocusChange || didStartDateChange || minimumNights !== prevMinimumNights) {
                const startSpan = prevStartDate || this.today;
                modifiers = this.deleteModifierFromRange(
                    modifiers,
                    startSpan,
                    cloneDateTime(startSpan).plus({ days: prevMinimumNights }),
                    'blocked-minimum-nights'
                );

                modifiers = this.deleteModifierFromRange(
                    modifiers,
                    startSpan,
                    cloneDateTime(startSpan).plus({ days: prevMinimumNights }),
                    'blocked'
                );
            }
        }

        if (didFocusChange || recomputePropModifiers) {
            values(visibleDays).forEach((days) => {
                Object.keys(days).forEach((day) => {
                    const dateTimeObj = getPooledLuxon(day); // Convert string to Luxon DateTime
                    let isBlocked = false;

                    if (didFocusChange || recomputeOutsideRange) {
                        if (isOutsideRange(dateTimeObj)) {
                            modifiers = this.addModifier(modifiers, dateTimeObj, 'blocked-out-of-range');
                            isBlocked = true;
                        } else {
                            modifiers = this.deleteModifier(modifiers, dateTimeObj, 'blocked-out-of-range');
                        }
                    }

                    if (didFocusChange || recomputeDayBlocked) {
                        if (isDayBlocked(dateTimeObj)) {
                            modifiers = this.addModifier(modifiers, dateTimeObj, 'blocked-calendar');
                            isBlocked = true;
                        } else {
                            modifiers = this.deleteModifier(modifiers, dateTimeObj, 'blocked-calendar');
                        }
                    }

                    if (isBlocked) {
                        modifiers = this.addModifier(modifiers, dateTimeObj, 'blocked');
                    } else {
                        modifiers = this.deleteModifier(modifiers, dateTimeObj, 'blocked');
                    }

                    if (didFocusChange || recomputeDayHighlighted) {
                        if (isDayHighlighted(dateTimeObj)) {
                            modifiers = this.addModifier(modifiers, dateTimeObj, 'highlighted-calendar');
                        } else {
                            modifiers = this.deleteModifier(modifiers, dateTimeObj, 'highlighted-calendar');
                        }
                    }
                });
            });
        }

        if (!this.isTouchDevice && didFocusChange && hoverDate && !this.isBlocked(hoverDate)) {
            const minNightsForHoverDate = getMinNightsForHoverDate(hoverDate);
            if (minNightsForHoverDate > 0 && focusedInput === END_DATE) {
                modifiers = this.deleteModifierFromRange(
                    modifiers,
                    cloneDateTime(hoverDate).plus({ days: 1 }),
                    cloneDateTime(hoverDate).plus({ days: minNightsForHoverDate }),
                    'hovered-start-blocked-minimum-nights'
                );

                modifiers = this.deleteModifier(
                    modifiers,
                    cloneDateTime(hoverDate).plus({ days: minNightsForHoverDate }),
                    'hovered-start-first-possible-end'
                );
            }

            if (minNightsForHoverDate > 0 && focusedInput === START_DATE) {
                modifiers = this.addModifierToRange(
                    modifiers,
                    cloneDateTime(hoverDate).plus({ days: 1 }),
                    cloneDateTime(hoverDate).plus({ days: minNightsForHoverDate }),
                    'hovered-start-blocked-minimum-nights'
                );

                modifiers = this.addModifier(
                    modifiers,
                    cloneDateTime(hoverDate).plus({ days: minNightsForHoverDate }),
                    'hovered-start-first-possible-end'
                );
            }
        }

        if (minimumNights > 0 && startDate && focusedInput === END_DATE) {
            modifiers = this.addModifierToRange(
                modifiers,
                startDate,
                cloneDateTime(startDate).plus({ days: minimumNights }),
                'blocked-minimum-nights'
            );

            modifiers = this.addModifierToRange(
                modifiers,
                startDate,
                cloneDateTime(startDate).plus({ days: minimumNights }),
                'blocked'
            );
        }

        const today = DateTime.now();
        if (!isSameDay(this.today, today)) {
            modifiers = this.deleteModifier(modifiers, this.today, 'today');
            modifiers = this.addModifier(modifiers, today, 'today');
            this.today = today;
        }

        if (Object.keys(modifiers).length > 0) {
            this.setState({
                visibleDays: {
                    ...visibleDays,
                    ...modifiers,
                },
            });
        }

        if (didFocusChange || phrases !== prevPhrases) {
            // set the appropriate CalendarDay phrase based on focusedInput
            const chooseAvailableDate = getChooseAvailableDatePhrase(phrases, focusedInput);

            this.setState({
                phrases: {
                    ...phrases,
                    chooseAvailableDate,
                },
            });
        }
    }

    onDayClick(day, e) {
        const {
            keepOpenOnDateSelect,
            minimumNights,
            onBlur,
            focusedInput,
            onFocusChange,
            onClose,
            onDatesChange,
            startDateOffset,
            endDateOffset,
            disabled,
            daysViolatingMinNightsCanBeClicked,
        } = this.props;

        if (e) e.preventDefault();

        if (this.isBlocked(day, !daysViolatingMinNightsCanBeClicked)) return;

        let { startDate, endDate } = this.props;

        if (startDateOffset || endDateOffset) {
            startDate = getSelectedDateOffset(startDateOffset, day);
            endDate = getSelectedDateOffset(endDateOffset, day);

            if (this.isBlocked(startDate) || this.isBlocked(endDate)) {
                return;
            }

            onDatesChange({ startDate, endDate });

            if (!keepOpenOnDateSelect) {
                onFocusChange(null);
                onClose({ startDate, endDate });
            }
        } else if (focusedInput === START_DATE) {
            const lastAllowedStartDate = endDate && cloneDateTime(endDate).minus({ days: minimumNights });
            const isStartDateAfterEndDate = isBeforeDay(lastAllowedStartDate, day) || isAfterDay(startDate, endDate);
            const isEndDateDisabled = disabled === END_DATE;

            if (!isEndDateDisabled || !isStartDateAfterEndDate) {
                startDate = day;
                if (isStartDateAfterEndDate) {
                    endDate = null;
                }
            }

            onDatesChange({ startDate, endDate });

            if (isEndDateDisabled && !isStartDateAfterEndDate) {
                onFocusChange(null);
                onClose({ startDate, endDate });
            } else if (!isEndDateDisabled) {
                onFocusChange(END_DATE);
            }
        } else if (focusedInput === END_DATE) {
            const firstAllowedEndDate = startDate && cloneDateTime(startDate).plus({ days: minimumNights });

            if (!startDate) {
                endDate = day;
                onDatesChange({ startDate, endDate });
                onFocusChange(START_DATE);
            } else if (isInclusivelyAfterDay(day, firstAllowedEndDate)) {
                endDate = day;
                onDatesChange({ startDate, endDate });
                if (!keepOpenOnDateSelect) {
                    onFocusChange(null);
                    onClose({ startDate, endDate });
                }
            } else if (daysViolatingMinNightsCanBeClicked && this.doesNotMeetMinimumNights(day)) {
                endDate = day;
                onDatesChange({ startDate, endDate });
            } else if (disabled !== START_DATE) {
                startDate = day;
                endDate = null;
                onDatesChange({ startDate, endDate });
            } else {
                onDatesChange({ startDate, endDate });
            }
        } else {
            onDatesChange({ startDate, endDate });
        }

        onBlur();
    }

    onDayMouseEnter(day) {
        /* eslint react/destructuring-assignment: 1 */
        if (this.isTouchDevice) return;
        const {
            startDate,
            endDate,
            focusedInput,
            getMinNightsForHoverDate,
            minimumNights,
            startDateOffset,
            endDateOffset,
        } = this.props;

        const { hoverDate, visibleDays, dateOffset } = this.state;

        let nextDateOffset = null;

        if (focusedInput) {
            const hasOffset = startDateOffset || endDateOffset;
            let modifiers = {};

            if (hasOffset) {
                const start = getSelectedDateOffset(startDateOffset, day);
                const end = getSelectedDateOffset(endDateOffset, day, (rangeDay) => rangeDay.plus({ days: 1 }));

                nextDateOffset = {
                    start,
                    end,
                };

                // eslint-disable-next-line react/destructuring-assignment
                if (dateOffset && dateOffset.start && dateOffset.end) {
                    modifiers = this.deleteModifierFromRange(
                        modifiers,
                        dateOffset.start,
                        dateOffset.end,
                        'hovered-offset'
                    );
                }
                modifiers = this.addModifierToRange(modifiers, start, end, 'hovered-offset');
            }

            if (!hasOffset) {
                modifiers = this.deleteModifier(modifiers, hoverDate, 'hovered');
                modifiers = this.addModifier(modifiers, day, 'hovered');

                if (startDate && !endDate && focusedInput === END_DATE) {
                    if (isAfterDay(hoverDate, startDate)) {
                        const endSpan = cloneDateTime(hoverDate).plus({ days: 1 });
                        modifiers = this.deleteModifierFromRange(modifiers, startDate, endSpan, 'hovered-span');
                    }

                    if (isBeforeDay(day, startDate) || isSameDay(day, startDate)) {
                        modifiers = this.deleteModifier(modifiers, startDate, 'selected-start-in-hovered-span');
                    }

                    if (!this.isBlocked(day) && isAfterDay(day, startDate)) {
                        const endSpan = cloneDateTime(day).plus({ days: 1 });
                        modifiers = this.addModifierToRange(modifiers, startDate, endSpan, 'hovered-span');
                        modifiers = this.addModifier(modifiers, startDate, 'selected-start-in-hovered-span');
                    }
                }

                if (!startDate && endDate && focusedInput === START_DATE) {
                    if (isBeforeDay(hoverDate, endDate)) {
                        modifiers = this.deleteModifierFromRange(modifiers, hoverDate, endDate, 'hovered-span');
                    }

                    if (isAfterDay(day, endDate) || isSameDay(day, endDate)) {
                        modifiers = this.deleteModifier(modifiers, endDate, 'selected-end-in-hovered-span');
                    }

                    if (!this.isBlocked(day) && isBeforeDay(day, endDate)) {
                        modifiers = this.addModifierToRange(modifiers, day, endDate, 'hovered-span');
                        modifiers = this.addModifier(modifiers, endDate, 'selected-end-in-hovered-span');
                    }
                }

                if (startDate) {
                    const startSpan = cloneDateTime(startDate).plus({ days: 1 });
                    const endSpan = cloneDateTime(startDate).plus({ days: minimumNights + 1 });
                    modifiers = this.deleteModifierFromRange(modifiers, startSpan, endSpan, 'after-hovered-start');

                    if (isSameDay(day, startDate)) {
                        const newStartSpan = cloneDateTime(startDate).plus({ days: 1 });
                        const newEndSpan = cloneDateTime(startDate).plus({ days: minimumNights + 1 });
                        modifiers = this.addModifierToRange(modifiers, newStartSpan, newEndSpan, 'after-hovered-start');
                    }
                }

                if (endDate) {
                    const startSpan = cloneDateTime(endDate).minus({ days: minimumNights });
                    modifiers = this.deleteModifierFromRange(modifiers, startSpan, endDate, 'before-hovered-end');

                    if (isSameDay(day, endDate)) {
                        const newStartSpan = cloneDateTime(endDate).minus({ days: minimumNights });
                        modifiers = this.addModifierToRange(modifiers, newStartSpan, endDate, 'before-hovered-end');
                    }
                }

                if (hoverDate && !this.isBlocked(hoverDate)) {
                    const minNightsForPrevHoverDate = getMinNightsForHoverDate(hoverDate);
                    if (minNightsForPrevHoverDate > 0 && focusedInput === START_DATE) {
                        modifiers = this.deleteModifierFromRange(
                            modifiers,
                            cloneDateTime(hoverDate).plus({ days: 1 }),
                            cloneDateTime(hoverDate).plus({ days: minNightsForPrevHoverDate }),
                            'hovered-start-blocked-minimum-nights'
                        );

                        modifiers = this.deleteModifier(
                            modifiers,
                            cloneDateTime(hoverDate).plus({ days: minNightsForPrevHoverDate }),
                            'hovered-start-first-possible-end'
                        );
                    }
                }

                if (!this.isBlocked(day)) {
                    const minNightsForHoverDate = getMinNightsForHoverDate(day);
                    if (minNightsForHoverDate > 0 && focusedInput === START_DATE) {
                        modifiers = this.addModifierToRange(
                            modifiers,
                            cloneDateTime(day).plus({ days: 1 }),
                            cloneDateTime(day).plus({ days: minNightsForHoverDate }),
                            'hovered-start-blocked-minimum-nights'
                        );

                        modifiers = this.addModifier(
                            modifiers,
                            cloneDateTime(day).plus({ days: minNightsForHoverDate }),
                            'hovered-start-first-possible-end'
                        );
                    }
                }
            }

            this.setState({
                hoverDate: day,
                dateOffset: nextDateOffset,
                visibleDays: {
                    ...visibleDays,
                    ...modifiers,
                },
            });
        }
    }

    onDayMouseLeave(day) {
        const { startDate, endDate, focusedInput, getMinNightsForHoverDate, minimumNights } = this.props;
        const { hoverDate, visibleDays, dateOffset } = this.state;

        if (this.isTouchDevice || !hoverDate) return;

        let modifiers = {};
        modifiers = this.deleteModifier(modifiers, hoverDate, 'hovered');

        if (dateOffset) {
            modifiers = this.deleteModifierFromRange(modifiers, dateOffset.start, dateOffset.end, 'hovered-offset');
        }

        if (startDate && !endDate) {
            if (isAfterDay(hoverDate, startDate)) {
                const endSpan = cloneDateTime(hoverDate).plus({ days: 1 });
                modifiers = this.deleteModifierFromRange(modifiers, startDate, endSpan, 'hovered-span');
            }

            if (isAfterDay(day, startDate)) {
                modifiers = this.deleteModifier(modifiers, startDate, 'selected-start-in-hovered-span');
            }
        }

        if (!startDate && endDate) {
            if (isAfterDay(endDate, hoverDate)) {
                modifiers = this.deleteModifierFromRange(modifiers, hoverDate, endDate, 'hovered-span');
            }

            if (isBeforeDay(day, endDate)) {
                modifiers = this.deleteModifier(modifiers, endDate, 'selected-end-in-hovered-span');
            }
        }

        if (startDate && isSameDay(day, startDate)) {
            const startSpan = cloneDateTime(startDate).plus({ days: 1 });
            const endSpan = cloneDateTime(startDate).plus({ days: minimumNights + 1 });
            modifiers = this.deleteModifierFromRange(modifiers, startSpan, endSpan, 'after-hovered-start');
        }

        if (endDate && isSameDay(day, endDate)) {
            const startSpan = cloneDateTime(endDate).minus({ days: minimumNights });
            modifiers = this.deleteModifierFromRange(modifiers, startSpan, endDate, 'before-hovered-end');
        }

        if (!this.isBlocked(hoverDate)) {
            const minNightsForHoverDate = getMinNightsForHoverDate(hoverDate);
            if (minNightsForHoverDate > 0 && focusedInput === START_DATE) {
                modifiers = this.deleteModifierFromRange(
                    modifiers,
                    cloneDateTime(hoverDate).plus({ days: 1 }),
                    cloneDateTime(hoverDate).plus({ days: minNightsForHoverDate }),
                    'hovered-start-blocked-minimum-nights'
                );

                modifiers = this.deleteModifier(
                    modifiers,
                    cloneDateTime(hoverDate).plus({ days: minNightsForHoverDate }),
                    'hovered-start-first-possible-end'
                );
            }
        }

        this.setState({
            hoverDate: null,
            visibleDays: {
                ...visibleDays,
                ...modifiers,
            },
        });
    }

    onPrevMonthClick() {
        const { enableOutsideDays, maxDate, minDate, numberOfMonths, onPrevMonthClick } = this.props;
        const { currentMonth, visibleDays } = this.state;

        const newVisibleDays = {};
        Object.keys(visibleDays)
            .sort()
            .slice(0, numberOfMonths + 1)
            .forEach((month) => {
                newVisibleDays[month] = visibleDays[month];
            });

        const prevMonth = cloneDateTime(currentMonth).minus({ months: 2 });
        const prevMonthVisibleDays = getVisibleDays(prevMonth, 1, enableOutsideDays, true);

        const newCurrentMonth = cloneDateTime(currentMonth).minus({ months: 1 });
        this.setState(
            {
                currentMonth: newCurrentMonth,
                disablePrev: this.shouldDisableMonthNavigation(minDate, newCurrentMonth),
                disableNext: this.shouldDisableMonthNavigation(maxDate, newCurrentMonth),
                visibleDays: {
                    ...newVisibleDays,
                    ...this.getModifiers(prevMonthVisibleDays),
                },
            },
            () => {
                onPrevMonthClick(cloneDateTime(newCurrentMonth));
            }
        );
    }

    onNextMonthClick() {
        const { enableOutsideDays, maxDate, minDate, numberOfMonths, onNextMonthClick } = this.props;
        const { currentMonth, visibleDays } = this.state;

        const newVisibleDays = {};
        Object.keys(visibleDays)
            .sort()
            .slice(1)
            .forEach((month) => {
                newVisibleDays[month] = visibleDays[month];
            });

        const nextMonth = cloneDateTime(currentMonth).plus({ months: numberOfMonths + 1 });
        const nextMonthVisibleDays = getVisibleDays(nextMonth, 1, enableOutsideDays, true);
        const newCurrentMonth = cloneDateTime(currentMonth).plus({ months: 1 });
        this.setState(
            {
                currentMonth: newCurrentMonth,
                disablePrev: this.shouldDisableMonthNavigation(minDate, newCurrentMonth),
                disableNext: this.shouldDisableMonthNavigation(maxDate, newCurrentMonth),
                visibleDays: {
                    ...newVisibleDays,
                    ...this.getModifiers(nextMonthVisibleDays),
                },
            },
            () => {
                onNextMonthClick(cloneDateTime(newCurrentMonth));
            }
        );
    }

    onMonthChange(newMonth) {
        const { numberOfMonths, enableOutsideDays, orientation } = this.props;
        const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;
        const newVisibleDays = getVisibleDays(newMonth, numberOfMonths, enableOutsideDays, withoutTransitionMonths);

        this.setState({
            currentMonth: cloneDateTime(newMonth),
            visibleDays: this.getModifiers(newVisibleDays),
        });
    }

    onYearChange(newMonth) {
        const { numberOfMonths, enableOutsideDays, orientation } = this.props;
        const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;
        const newVisibleDays = getVisibleDays(newMonth, numberOfMonths, enableOutsideDays, withoutTransitionMonths);

        this.setState({
            currentMonth: cloneDateTime(newMonth),
            visibleDays: this.getModifiers(newVisibleDays),
        });
    }

    onGetNextScrollableMonths() {
        const { numberOfMonths, enableOutsideDays } = this.props;
        const { currentMonth, visibleDays } = this.state;

        const numberOfVisibleMonths = Object.keys(visibleDays).length;
        const nextMonth = cloneDateTime(currentMonth).plus({ months: numberOfVisibleMonths });
        const newVisibleDays = getVisibleDays(nextMonth, numberOfMonths, enableOutsideDays, true);

        this.setState({
            visibleDays: {
                ...visibleDays,
                ...this.getModifiers(newVisibleDays),
            },
        });
    }

    onGetPrevScrollableMonths() {
        const { numberOfMonths, enableOutsideDays } = this.props;
        const { currentMonth, visibleDays } = this.state;

        const firstPreviousMonth = cloneDateTime(currentMonth).minus({ months: numberOfMonths });
        const newVisibleDays = getVisibleDays(firstPreviousMonth, numberOfMonths, enableOutsideDays, true);

        this.setState({
            currentMonth: cloneDateTime(firstPreviousMonth),
            visibleDays: {
                ...visibleDays,
                ...this.getModifiers(newVisibleDays),
            },
        });
    }

    getFirstFocusableDay(newMonth) {
        const { startDate, endDate, focusedInput, minimumNights, numberOfMonths } = this.props;

        let focusedDate = cloneDateTime(newMonth).startOf('month').set({ hours: 12 });
        if (focusedInput === START_DATE && startDate) {
            focusedDate = cloneDateTime(startDate);
        } else if (focusedInput === END_DATE && !endDate && startDate) {
            focusedDate = cloneDateTime(startDate).plus({ days: minimumNights });
        } else if (focusedInput === END_DATE && endDate) {
            focusedDate = cloneDateTime(endDate);
        }

        if (this.isBlocked(focusedDate)) {
            const days = [];
            const lastVisibleDay = cloneDateTime(newMonth)
                .plus({ months: numberOfMonths - 1 })
                .endOf('month');
            let currentDay = cloneDateTime(focusedDate);
            while (!isAfterDay(currentDay, lastVisibleDay)) {
                currentDay = cloneDateTime(currentDay).plus({ days: 1 });
                days.push(currentDay);
            }

            const viableDays = days.filter((day) => !this.isBlocked(day));

            if (viableDays.length > 0) {
                [focusedDate] = viableDays;
            }
        }

        return focusedDate;
    }

    getModifiers(visibleDays) {
        const modifiers = {};
        Object.keys(visibleDays).forEach((month) => {
            modifiers[month] = {};
            visibleDays[month].forEach((day) => {
                modifiers[month][toISODateString(day)] = this.getModifiersForDay(day);
            });
        });

        return modifiers;
    }

    getModifiersForDay(day) {
        return new Set(Object.keys(this.modifiers).filter((modifier) => this.modifiers[modifier](day)));
    }

    getStateForNewMonth(nextProps) {
        const { initialVisibleMonth, numberOfMonths, enableOutsideDays, orientation, startDate } = nextProps;
        const initialVisibleMonthThunk = initialVisibleMonth || (startDate ? () => startDate : () => this.today);
        const currentMonth = initialVisibleMonthThunk();
        const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;

        const visibleDays = this.getModifiers(
            getVisibleDays(currentMonth, numberOfMonths, enableOutsideDays, withoutTransitionMonths)
        );
        return { currentMonth, visibleDays };
    }

    shouldDisableMonthNavigation(date, visibleMonth) {
        if (!date) return false;

        const { numberOfMonths, enableOutsideDays } = this.props;

        return isDayVisible(date, visibleMonth, numberOfMonths, enableOutsideDays);
    }

    addModifier(updatedDays, day, modifier) {
        return addModifier(updatedDays, day, modifier, this.props, this.state);
    }

    addModifierToRange(updatedDays, start, end, modifier) {
        let days = updatedDays;

        let spanStart = cloneDateTime(start);
        while (isBeforeDay(spanStart, end)) {
            days = this.addModifier(days, spanStart, modifier);
            spanStart = cloneDateTime(spanStart).plus({ days: 1 });
        }

        return days;
    }

    deleteModifier(updatedDays, day, modifier) {
        return deleteModifier(updatedDays, day, modifier, this.props, this.state);
    }

    deleteModifierFromRange(updatedDays, start, end, modifier) {
        let days = updatedDays;

        let spanStart = cloneDateTime(start);
        while (isBeforeDay(spanStart, end)) {
            days = this.deleteModifier(days, spanStart, modifier);
            spanStart = cloneDateTime(spanStart).plus({ days: 1 });
        }

        return days;
    }

    doesNotMeetMinimumNights(day) {
        const { startDate, isOutsideRange, focusedInput, minimumNights } = this.props;
        if (focusedInput !== END_DATE) return false;

        if (startDate) {
            const startDay = cloneDateTime(startDate).startOf('day').set({ hour: 12 });
            const dayDiff = day.diff(startDay, 'days').days;
            return dayDiff < minimumNights && dayDiff >= 0;
        }

        const compareDate = day.minus({ days: minimumNights });
        return isOutsideRange(compareDate);
    }

    doesNotMeetMinNightsForHoveredStartDate(day, hoverDate) {
        const { focusedInput, getMinNightsForHoverDate } = this.props;
        if (focusedInput !== END_DATE) return false;

        if (hoverDate && !this.isBlocked(hoverDate)) {
            const minNights = getMinNightsForHoverDate(hoverDate);
            const dayDiff = day.diff(cloneDateTime(hoverDate).startOf('day').set({ hours: 12 }), 'days').days;
            return dayDiff < minNights && dayDiff >= 0;
        }
        return false;
    }

    isDayAfterHoveredStartDate(day) {
        const { startDate, endDate, minimumNights } = this.props;
        const { hoverDate } = this.state || {};
        return (
            !!startDate &&
            !endDate &&
            !this.isBlocked(day) &&
            isNextDay(hoverDate, day) &&
            minimumNights > 0 &&
            isSameDay(hoverDate, startDate)
        );
    }

    isEndDate(day) {
        const { endDate } = this.props;
        return isSameDay(day, endDate);
    }

    isHovered(day) {
        const { hoverDate } = this.state || {};
        const { focusedInput } = this.props;
        return !!focusedInput && isSameDay(day, hoverDate);
    }

    // old
    // isInHoveredSpan(day) {
    //   const { startDate, endDate } = this.props;
    //   const { hoverDate } = this.state || {};

    //   const isForwardRange = !!startDate && !endDate && (
    //     day.isBetween(startDate, hoverDate) || isSameDay(hoverDate, day)
    //   );
    //   const isBackwardRange = !!endDate && !startDate && (
    //     day.isBetween(hoverDate, endDate) || isSameDay(hoverDate, day)
    //   );

    //   const isValidDayHovered = hoverDate && !this.isBlocked(hoverDate);

    //   return (isForwardRange || isBackwardRange) && isValidDayHovered;
    // }

    // new
    isInHoveredSpan(day) {
        const { startDate, endDate } = this.props;
        const { hoverDate } = this.state || {};
        const isStartDateValid = startDate && startDate.isValid;
        const isEndDateValid = endDate && endDate.isValid;
        const isHoverDateValid = hoverDate && hoverDate.isValid;

        if (!isStartDateValid || !isEndDateValid || !isHoverDateValid) {
            return false;
        }

        const isForwardRange =
            !!startDate && !endDate && ((day >= startDate && day <= hoverDate) || day.equals(hoverDate));
        const isBackwardRange =
            !!endDate && !startDate && ((day >= hoverDate && day <= endDate) || day.equals(hoverDate));

        const isValidDayHovered = hoverDate && !this.isBlocked(hoverDate);

        return (isForwardRange || isBackwardRange) && isValidDayHovered;
    }

    isInSelectedSpan(day) {
        const { startDate, endDate } = this.props;
        // if (!startDate || !endDate) {
        //   return false;
        // }
        return day > startDate && day < endDate;
    }

    isLastInRange(day) {
        const { endDate } = this.props;
        return this.isInSelectedSpan(day) && isNextDay(day, endDate);
    }

    isStartDate(day) {
        const { startDate } = this.props;
        return isSameDay(day, startDate);
    }

    isBlocked(day, blockDaysViolatingMinNights = true) {
        const { isDayBlocked, isOutsideRange } = this.props;
        return (
            isDayBlocked(day) ||
            isOutsideRange(day) ||
            (blockDaysViolatingMinNights && this.doesNotMeetMinimumNights(day))
        );
    }

    isToday(day) {
        return isSameDay(day, this.today);
    }

    isFirstPossibleEndDateForHoveredStartDate(day, hoverDate) {
        const { focusedInput, getMinNightsForHoverDate } = this.props;
        if (focusedInput !== END_DATE || !hoverDate || this.isBlocked(hoverDate)) return false;
        const minNights = getMinNightsForHoverDate(hoverDate);
        const firstAvailableEndDate = cloneDateTime(hoverDate).plus({ days: minNights });
        return isSameDay(day, firstAvailableEndDate);
    }

    beforeSelectedEnd(day) {
        const { endDate } = this.props;
        return isBeforeDay(day, endDate);
    }

    isDayBeforeHoveredEndDate(day) {
        const { startDate, endDate, minimumNights } = this.props;
        const { hoverDate } = this.state || {};

        return (
            !!endDate &&
            !startDate &&
            !this.isBlocked(day) &&
            isPreviousDay(hoverDate, day) &&
            minimumNights > 0 &&
            isSameDay(hoverDate, endDate)
        );
    }

    render() {
        const {
            numberOfMonths,
            orientation,
            monthFormat,
            renderMonthText,
            renderWeekHeaderElement,
            dayPickerNavigationInlineStyles,
            navPosition,
            navPrev,
            navNext,
            renderNavPrevButton,
            renderNavNextButton,
            noNavButtons,
            noNavNextButton,
            noNavPrevButton,
            onOutsideClick,
            withPortal,
            enableOutsideDays,
            firstDayOfWeek,
            renderKeyboardShortcutsButton,
            renderKeyboardShortcutsPanel,
            hideKeyboardShortcutsPanel,
            daySize,
            focusedInput,
            renderCalendarDay,
            renderDayContents,
            renderCalendarInfo,
            renderMonthElement,
            calendarInfoPosition,
            onBlur,
            onShiftTab,
            onTab,
            isFocused,
            showKeyboardShortcuts,
            isRTL,
            weekDayFormat,
            dayAriaLabelFormat,
            verticalHeight,
            noBorder,
            transitionDuration,
            verticalBorderSpacing,
            horizontalMonthPadding,
        } = this.props;

        const { currentMonth, phrases, visibleDays, disablePrev, disableNext } = this.state;

        return (
            <DayPicker
                orientation={orientation}
                enableOutsideDays={enableOutsideDays}
                modifiers={visibleDays}
                numberOfMonths={numberOfMonths}
                onDayClick={this.onDayClick}
                onDayMouseEnter={this.onDayMouseEnter}
                onDayMouseLeave={this.onDayMouseLeave}
                onPrevMonthClick={this.onPrevMonthClick}
                onNextMonthClick={this.onNextMonthClick}
                onMonthChange={this.onMonthChange}
                onTab={onTab}
                onShiftTab={onShiftTab}
                onYearChange={this.onYearChange}
                onGetNextScrollableMonths={this.onGetNextScrollableMonths}
                onGetPrevScrollableMonths={this.onGetPrevScrollableMonths}
                monthFormat={monthFormat}
                renderMonthText={renderMonthText}
                renderWeekHeaderElement={renderWeekHeaderElement}
                withPortal={withPortal}
                hidden={!focusedInput}
                initialVisibleMonth={() => currentMonth}
                daySize={daySize}
                onOutsideClick={onOutsideClick}
                disablePrev={disablePrev}
                disableNext={disableNext}
                dayPickerNavigationInlineStyles={dayPickerNavigationInlineStyles}
                navPosition={navPosition}
                navPrev={navPrev}
                navNext={navNext}
                renderNavPrevButton={renderNavPrevButton}
                renderNavNextButton={renderNavNextButton}
                noNavButtons={noNavButtons}
                noNavPrevButton={noNavPrevButton}
                noNavNextButton={noNavNextButton}
                renderCalendarDay={renderCalendarDay}
                renderDayContents={renderDayContents}
                renderCalendarInfo={renderCalendarInfo}
                renderMonthElement={renderMonthElement}
                renderKeyboardShortcutsButton={renderKeyboardShortcutsButton}
                renderKeyboardShortcutsPanel={renderKeyboardShortcutsPanel}
                calendarInfoPosition={calendarInfoPosition}
                firstDayOfWeek={firstDayOfWeek}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                isFocused={isFocused}
                getFirstFocusableDay={this.getFirstFocusableDay}
                onBlur={onBlur}
                showKeyboardShortcuts={showKeyboardShortcuts}
                phrases={phrases}
                isRTL={isRTL}
                weekDayFormat={weekDayFormat}
                dayAriaLabelFormat={dayAriaLabelFormat}
                verticalHeight={verticalHeight}
                verticalBorderSpacing={verticalBorderSpacing}
                noBorder={noBorder}
                transitionDuration={transitionDuration}
                horizontalMonthPadding={horizontalMonthPadding}
            />
        );
    }
}

DayPickerRangeController.propTypes = propTypes;
DayPickerRangeController.defaultProps = defaultProps;
